import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Provider from 'provider';
import logging from 'utils/logging';

const log = logging.getLogger('TriviaFacts');

const Container = styled(Box)({
  marginTop: 28,
  padding: '20px 24px',
  borderRadius: 12,
  background: 'rgba(46,196,182,0.06)',
  border: '1px dashed rgba(46,196,182,0.35)',
});

const SectionLabel = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.7rem',
  letterSpacing: '0.32em',
  color: '#1a7a72',
  textTransform: 'uppercase',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: 10,
});

const ContentWrap = styled(Box, { shouldForwardProp: (p) => p !== 'swapping' })(({ swapping }) => ({
  transition: 'opacity 0.8s cubic-bezier(.4,0,.2,1), transform 0.8s cubic-bezier(.4,0,.2,1)',
  opacity: swapping ? 0 : 1,
  transform: swapping ? 'translateY(-6px)' : 'translateY(0)',
}));

const Question = styled(Typography)({
  fontSize: '1rem',
  color: '#333',
  textAlign: 'center',
  lineHeight: 1.4,
  minHeight: '1.4em',
});

const Answer = styled(Typography, { shouldForwardProp: (p) => p !== 'visible' })(({ visible }) => ({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '1.4rem',
  color: '#1a7a72',
  textAlign: 'center',
  lineHeight: 1.15,
  marginTop: 8,
  letterSpacing: '0.04em',
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.6s ease-out',
  minHeight: '1.15em',
}));

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const TriviaFacts = () => {
  const [facts, setFacts] = useState(null);
  const [factIndex, setFactIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [swapping, setSwapping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Provider.fetchApprovedTriviaFacts(50)
      .then((rows) => { if (!cancelled) setFacts(shuffle(rows)); })
      .catch((err) => { log.warn('Failed to load trivia facts', err); if (!cancelled) setFacts([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!facts || facts.length === 0) return undefined;
    setShowAnswer(false);
    const t = setTimeout(() => setShowAnswer(true), 4000);
    return () => clearTimeout(t);
  }, [factIndex, facts]);

  useEffect(() => {
    if (!facts || facts.length <= 1) return undefined;
    const interval = setInterval(() => {
      setSwapping(true);
      setTimeout(() => {
        setFactIndex((i) => (i + 1) % facts.length);
        setSwapping(false);
      }, 800);
    }, 9000);
    return () => clearInterval(interval);
  }, [facts]);

  if (!facts || facts.length === 0) return null;
  const fact = facts[factIndex];

  return (
    <Container>
      <SectionLabel>Did You Know</SectionLabel>
      <ContentWrap swapping={swapping}>
        <Question>{fact.question}</Question>
        <Answer visible={showAnswer}>{showAnswer ? fact.answer : ' '}</Answer>
      </ContentWrap>
    </Container>
  );
};

export default TriviaFacts;
